"use client";

import { createContext, useCallback, useContext, useRef, useState } from "react";
import Icon, { type IconName } from "./Icon";

type ToastTone = "ok" | "accent" | "warn" | "danger";
interface Toast {
  id: number;
  title: string;
  tone: ToastTone;
  icon?: IconName;
}

interface Ctx {
  toast: (t: Omit<Toast, "id">) => void;
}

const ToastCtx = createContext<Ctx | null>(null);

const toneColor: Record<ToastTone, string> = {
  ok: "var(--ok)",
  accent: "var(--accent)",
  warn: "var(--warn)",
  danger: "var(--danger)",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const toast = useCallback((t: Omit<Toast, "id">) => {
    const id = ++seq.current;
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 3200);
  }, []);

  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-5 right-5 z-[60] flex flex-col gap-3 w-[min(92vw,340px)]">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="glass slide-in-right rounded-2xl px-4 py-3.5 flex items-center gap-3"
          >
            <span
              className="chip grid place-items-center w-9 h-9 rounded-xl shrink-0"
              style={{ color: toneColor[t.tone], "--tint-color": `var(--mint)` } as React.CSSProperties}
            >
              <Icon name={t.icon ?? "bell"} size={17} />
            </span>
            <p className="text-[13.5px] font-medium text-text">{t.title}</p>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
